import { format, isValid, parse } from 'date-fns';
import { z } from 'zod';

import type { ColumnDef } from './columns';

/** The slice of a column or field descriptor the value checks read. */
export type FieldRules = Pick<
    ColumnDef,
    | 'kind'
    | 'label'
    | 'required'
    | 'choices'
    | 'ge'
    | 'le'
    | 'minLength'
    | 'maxLength'
    | 'pattern'
>;

export const ISO_DATE = 'yyyy-MM-dd';
const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a stored `YYYY-MM-DD` into a local-midnight `Date`; `new Date(str)` would read it as UTC and can shift a day. */
export function isoToDate(value: unknown): Date | undefined {
    if (typeof value !== 'string' || !ISO_DATE_SHAPE.test(value)) {
        return undefined;
    }
    const parsed = parse(value, ISO_DATE, new Date());
    return isValid(parsed) ? parsed : undefined;
}

/** The `YYYY-MM-DD` a picked `Date` is stored as. */
export function dateToIso(date: Date): string {
    return format(date, ISO_DATE);
}

/** Whether a value counts as unfilled for a field of this kind. */
export function isBlankValue(value: unknown, kind: ColumnDef['kind']): boolean {
    switch (kind) {
        case 'bool':
            return typeof value !== 'boolean';
        case 'number':
        case 'integer':
        case 'decimal':
            if (typeof value === 'number') return Number.isNaN(value);
            return typeof value !== 'string' || !value.trim();
        case 'list':
            return !Array.isArray(value) || value.length === 0;
        default:
            return typeof value !== 'string' || !value.trim();
    }
}

/** Labels of the required custom columns a row leaves empty; any makes its group unused until filled. */
export function emptyRequiredLabels(
    columns: readonly ColumnDef[],
    payload: Record<string, unknown>,
): string[] {
    return columns
        .filter((c) => c.custom && c.required && isBlankValue(payload[c.key], c.kind))
        .map((c) => c.label);
}

function isBlank(value: unknown): boolean {
    return value == null || (typeof value === 'string' && !value.trim());
}

function numberOf(value: unknown): number {
    if (typeof value === 'number') return value;
    return typeof value === 'string' && value.trim() ? Number(value) : NaN;
}

function rangeError(
    label: string,
    n: number,
    { ge, le }: Pick<FieldRules, 'ge' | 'le'>,
): string | null {
    if (ge != null && n < ge) return `${label} must be at least ${ge}.`;
    if (le != null && n > le) return `${label} must be at most ${le}.`;
    return null;
}

function numberError(rules: FieldRules, value: unknown): string | null {
    const n = numberOf(value);
    if (!Number.isFinite(n)) return `${rules.label} must be a number.`;
    if (rules.kind === 'integer' && !Number.isInteger(n)) {
        return `${rules.label} must be a whole number.`;
    }
    return rangeError(rules.label, n, rules);
}

function textError(rules: FieldRules, value: unknown): string | null {
    if (typeof value !== 'string') return `${rules.label} must be text.`;
    const { label, minLength, maxLength } = rules;
    if (minLength != null && value.length < minLength) {
        return `${label} must be at least ${minLength} characters.`;
    }
    if (maxLength != null && value.length > maxLength) {
        return `${label} must be at most ${maxLength} characters.`;
    }
    // The pattern is enforced by the server only: the browser and server regex engines differ.
    return null;
}

/**
 * The message for a custom field's value, or `null` when it is acceptable.
 * Mirrors the backend model: a blank value is always fine (a required field
 * is a hint, never a rule), and a filled value must satisfy its type and limits. The server stays the
 * authority; this only saves the round trip.
 */
export function customValueError(
    rules: FieldRules,
    value: unknown,
): string | null {
    const filled =
        rules.kind === 'bool' ? typeof value === 'boolean' : !isBlank(value);
    if (!filled) return null;
    switch (rules.kind) {
        case 'bool':
            return null;
        case 'integer':
        case 'decimal':
        case 'number':
            return numberError(rules, value);
        case 'date':
            return isoToDate(value)
                ? null
                : `${rules.label} must be a valid date.`;
        case 'enum':
            return rules.choices?.includes(String(value))
                ? null
                : `${rules.label} must be one of the listed choices.`;
        case 'text':
        case 'long_text':
            return textError(rules, value);
        default:
            return null;
    }
}

/** Show a stored value read-only (earlier-version keys have no known type). */
export function formatReadOnlyValue(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

/** Replace blank (empty or whitespace-only) strings with `null` on custom keys so an empty optional field is sent as unset. */
export function nullBlankCustomValues(
    payload: Record<string, unknown>,
    customKeys: ReadonlySet<string>,
): Record<string, unknown> {
    const out = { ...payload };
    for (const key of customKeys) {
        const v = out[key];
        if (typeof v === 'string' && !v.trim()) out[key] = null;
    }
    return out;
}

/**
 * A zod object schema generated from field descriptors, so a form resolver runs
 * the same checks as the grid. Every key's value is checked by {@link customValueError}.
 */
export function buildCustomFieldsSchema(fields: readonly ColumnDef[]) {
    const shape: Record<string, z.ZodType<unknown>> = {};
    for (const field of fields) {
        shape[field.key] = z.unknown().superRefine((value, ctx) => {
            const message = customValueError(field, value);
            if (message) ctx.addIssue({ code: 'custom', message });
        });
    }
    return z.object(shape);
}
